import type { SagaIterator, Task } from 'redux-saga';
import { call, fork, join, put, select } from 'redux-saga/effects';
import type { RefreshTokenResponseDto } from '@poker-stats/contracts';

type RefreshResult = {
  success?: { payload: RefreshTokenResponseDto };
  reject?: unknown;
};

type SessionRefreshDependencies<State> = {
  selectAccessToken: (state: State) => string | undefined;
  selectRefreshToken: (state: State) => string | undefined;
  refresh: (token: string) => SagaIterator<RefreshResult> | Promise<RefreshResult>;
  saveTokens: (tokens: RefreshTokenResponseDto) => { type: string };
  logout: () => { type: string };
};

export function createSessionRefresh<State>(dependencies: SessionRefreshDependencies<State>) {
  let pending: { refreshToken: string; task: Task<boolean> } | undefined;
  let loggedOut = false;

  function* rejectCurrentSession(expectedRefreshToken?: string): SagaIterator<boolean> {
    const currentRefreshToken: string | undefined = yield select(dependencies.selectRefreshToken);
    if (currentRefreshToken === expectedRefreshToken && !loggedOut) {
      loggedOut = true;
      yield put(dependencies.logout());
    }
    return false;
  }

  function* refreshSession(refreshToken: string): SagaIterator<boolean> {
    let result: RefreshResult;
    try {
      result = yield call(dependencies.refresh, refreshToken);
    } catch {
      return yield call(rejectCurrentSession, refreshToken);
    }

    const currentRefreshToken: string | undefined = yield select(dependencies.selectRefreshToken);
    if (currentRefreshToken !== refreshToken) {
      return false;
    }

    const tokens = result?.success?.payload;
    if (
      !tokens ||
      typeof tokens.accessToken !== 'string' ||
      !tokens.accessToken ||
      typeof tokens.refreshToken !== 'string' ||
      !tokens.refreshToken
    ) {
      return yield call(rejectCurrentSession, refreshToken);
    }

    yield put(dependencies.saveTokens(tokens));
    return true;
  }

  return function* ensureFreshSession(
    initialAccessToken?: string,
    initialRefreshToken?: string,
  ): SagaIterator<boolean> {
    const currentAccessToken: string | undefined = yield select(dependencies.selectAccessToken);
    if (currentAccessToken && currentAccessToken !== initialAccessToken) {
      return true;
    }

    const currentRefreshToken: string | undefined = yield select(dependencies.selectRefreshToken);
    if (currentRefreshToken !== initialRefreshToken) {
      return false;
    }
    if (!currentRefreshToken) {
      return yield call(rejectCurrentSession, undefined);
    }

    loggedOut = false;
    if (!pending || pending.refreshToken !== currentRefreshToken || !pending.task.isRunning()) {
      const task: Task<boolean> = yield fork(refreshSession, currentRefreshToken);
      pending = { refreshToken: currentRefreshToken, task };
    }
    return yield join(pending.task);
  };
}
